import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { Input } from "@/components/ui/input";
import { LOGO } from "@/lib/brand";

const IMG = "https://images.unsplash.com/photo-1570786032462-2efc3ca8fccd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB3b3JzaGlwJTIwY29uY2VydHxlbnwwfHx8fDE3ODc5Njk4OTh8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { logo: brandLogo } = useBrand();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A1030]">
      <div className="relative hidden lg:block grain overflow-hidden">
        <img src={IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1030] via-[#0A1030]/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <p className="overline text-[#E6B34A] mb-3">In His Image Radio · Gospel</p>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-tight max-w-md">"Sing to the Lord a new song."</h2>
          <p className="text-zinc-400 mt-3">Psalm 96:1</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex flex-col items-center text-center mb-8">
            <img src={brandLogo} alt="In His Image Radio" className="w-28 h-28 object-contain logo-float mb-5" data-testid="login-logo" />
            <span className="font-display font-extrabold text-2xl tracking-tight">In His Image Radio</span>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-1 text-center">Welcome back</h1>
          <p className="text-zinc-400 mb-8 text-center">Sign in to save favorites and join the chat.</p>

          {error && <div className="mb-4 text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl px-4 py-3" data-testid="login-error">{error}</div>}

          <div className="space-y-4">
            <div><label className="overline text-zinc-400">Email</label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12" /></div>
            <div><label className="overline text-zinc-400">Password</label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12" /></div>
          </div>

          <button type="submit" disabled={loading} data-testid="login-submit" className="w-full mt-6 h-12 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </button>

          <p className="text-sm text-zinc-400 mt-6 text-center">No account? <Link to="/register" className="text-[#E6B34A] font-semibold" data-testid="to-register">Create one</Link></p>
          <div className="flex items-center justify-center gap-4 mt-3 text-sm">
            <Link to="/reset-password" className="text-zinc-400 hover:text-[#E6B34A]" data-testid="to-reset">Forgot password?</Link>
            <span className="text-zinc-600">·</span>
            <Link to="/forgot-account" className="text-zinc-400 hover:text-[#E6B34A]" data-testid="to-forgot-account">Forgot your email?</Link>
          </div>
          <p className="text-xs text-zinc-600 mt-8 text-center"><Link to="/" className="hover:text-zinc-400">← Continue as guest</Link></p>

          <p className="text-[11px] leading-relaxed text-zinc-600 mt-6 text-center border-t border-white/5 pt-5" data-testid="login-sms-notice">
            If you opted in to texts, In His Image Radio sends recurring automated messages (devotionals, show &amp; event reminders). Msg &amp; data rates may apply; msg frequency varies. Reply <span className="text-zinc-500 font-semibold">STOP</span> to opt out anytime, or <span className="text-zinc-500 font-semibold">HELP</span> for help. You can also manage texts in your profile. <Link to="/terms" className="text-[#E6B34A]/80 hover:underline">Terms</Link> · <Link to="/privacy" className="text-[#E6B34A]/80 hover:underline">Privacy</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
